/**
 * The composer's attached files (TMMAPP-0142 items 25a, §G U5), above the message box:
 * - each file as a chip: an image's thumbnail or the kind's icon, the name and size, the PDF's pages
 *   or the sheet's rows once served, and how it goes to the AI ("Sent as is" for pictures and PDFs,
 *   "Masked" for spreadsheets, from the upload's `sentAsIs`); progress while it uploads, the reason
 *   with Retry when it failed, "Expired" with Re-attach once the server swept it; Remove on each;
 * - the files the browser refused, each with its reason;
 * - the attach line ("Pictures and PDFs are sent to the AI as they are…") while files are attached;
 * - the pointer to Import when a bank file (CSV, OFX, QFX) is attached or tried.
 * A file that lands, fails (with its reason) or expires is announced politely. Removing a chip moves
 * focus to the chip now in its place, else the one before it, else back to the paperclip.
 */
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '@/components/icons';
import { LiveRegion } from '@/components/LiveRegion';
import { Pill } from '@/components/Pill';
import { formatByteSize, formatNumber } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { AttachmentFacts, AttachmentKindIcon } from './AttachmentFacts';
import {
  attachmentErrorCopy,
  ATTACHMENT_FILE_MAX_BYTES,
  ATTACHMENT_MESSAGE_MAX_FILES,
} from './attachmentRules';
import type { AttachmentRefusal, AttachmentsController, PendingAttachment } from './useAttachments';

function refusalText(
  t: ReturnType<typeof useTranslation>['t'],
  refusal: AttachmentRefusal,
  locale: string,
): string {
  switch (refusal.reason) {
    case 'file':
      return t('assistant.attach.limit.file', {
        limit: formatByteSize(ATTACHMENT_FILE_MAX_BYTES, locale),
      });
    case 'message':
      return t('assistant.attach.limit.message', {
        limit: formatNumber(ATTACHMENT_MESSAGE_MAX_FILES, locale),
      });
    default:
      return t('assistant.attach.error.type');
  }
}

function PendingChip({
  item,
  locked,
  onRemove,
  onReattach,
}: {
  item: PendingAttachment;
  /** A message is sending: Remove, Retry and Re-attach wait (K2). */
  locked: boolean;
  onRemove: () => void;
  onReattach: () => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const served = item.attachment;
  return (
    <Box
      data-attachment-chip={item.status}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 0.5,
        width: 220,
        minWidth: 0,
        border: `1px solid ${tokens.line}`,
        borderRadius: 1.5,
        bgcolor: tokens.surface,
      }}
    >
      {item.previewUrl !== null ? (
        <Box
          component="img"
          src={item.previewUrl}
          alt=""
          sx={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
        />
      ) : (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            bgcolor: tokens.surface2,
            color: tokens.text,
          }}
        >
          <AttachmentKindIcon kind={item.kind} />
        </Box>
      )}
      <Stack sx={{ flex: 1, minWidth: 0, gap: 0.125 }}>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.file.name}
        </Typography>
        <Stack direction="row" sx={{ alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}>
          <AttachmentFacts
            sizeBytes={served?.sizeBytes ?? item.file.size}
            pages={served?.pages ?? null}
            rows={served?.rows ?? null}
            truncated={served?.truncated ?? false}
          />
          {item.status === 'ready' && served !== null && (
            <Box component="span" data-sent-as={served.sentAsIs ? 'as-is' : 'masked'}>
              {served.sentAsIs ? (
                <Pill tone="warning">{t('assistant.attach.sentAsIs')}</Pill>
              ) : (
                <Pill wash="grey">{t('assistant.attach.masked')}</Pill>
              )}
            </Box>
          )}
        </Stack>
        {item.status === 'uploading' && (
          <LinearProgress
            aria-label={t('assistant.attach.uploading', { name: item.file.name })}
            {...(item.progress === null
              ? { variant: 'indeterminate' as const }
              : { variant: 'determinate' as const, value: item.progress })}
            sx={{ mt: 0.5 }}
          />
        )}
        {item.status === 'failed' && (
          <Stack direction="row" sx={{ alignItems: 'center', columnGap: 0.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12, color: tokens.errorText }} data-attachment-error="">
              {attachmentErrorCopy(t, item.error, locale)}
            </Typography>
            <Button size="small" disabled={locked} onClick={onReattach} sx={{ minWidth: 0, py: 0 }}>
              {t('common.retry')}
            </Button>
          </Stack>
        )}
        {item.status === 'expired' && (
          <Stack direction="row" sx={{ alignItems: 'center', columnGap: 0.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12, color: tokens.text2 }} data-attachment-expired="">
              {t('assistant.attach.expired')}
            </Typography>
            <Button size="small" disabled={locked} onClick={onReattach} sx={{ minWidth: 0, py: 0 }}>
              {t('assistant.attach.reattach')}
            </Button>
          </Stack>
        )}
      </Stack>
      <IconButton
        size="small"
        aria-label={t('assistant.attach.remove', { name: item.file.name })}
        disabled={locked}
        onClick={onRemove}
        data-attachment-remove=""
        sx={{ color: tokens.text2 }}
      >
        <CloseIcon size={16} />
      </IconButton>
    </Box>
  );
}

/** `key:status` per chip, to tell which chips changed since the last render. */
function statusesOf(items: readonly PendingAttachment[]): string {
  return items.map((item) => `${item.key}:${item.status}`).join(',');
}

export function AttachmentTray({
  attachments,
  locked = false,
  onEmpty,
}: {
  attachments: AttachmentsController;
  /** A message is sending: nothing is removed or re-attached (K2). */
  locked?: boolean | undefined;
  /** The last chip was removed: focus goes back to the control that attaches. */
  onEmpty: () => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const { items, refusals } = attachments;
  const listRef = useRef<HTMLDivElement | null>(null);
  // The removed chip's place: focus moves to the chip now there, else the one before it.
  const focusAt = useRef<number | null>(null);
  const [announcement, setAnnouncement] = useState({ key: 0, text: '' });

  // A file that finished uploading, failed or expired is announced politely, once.
  const statuses = statusesOf(items);
  const [seen, setSeen] = useState(statuses);
  if (statuses !== seen) {
    const before = new Map(
      seen
        .split(',')
        .filter((pair) => pair.length > 0)
        .map((pair) => {
          const [key, status] = pair.split(':');
          return [Number(key), status] as const;
        }),
    );
    setSeen(statuses);
    const lines = items.flatMap((item) => {
      if (before.get(item.key) === item.status) return [];
      const name = item.file.name;
      if (item.status === 'ready') return [t('assistant.attach.announce.ready', { name })];
      if (item.status === 'expired') return [t('assistant.attach.announce.expired', { name })];
      if (item.status === 'failed') {
        return [
          t('assistant.attach.announce.failed', {
            name,
            reason: attachmentErrorCopy(t, item.error, locale),
          }),
        ];
      }
      return [];
    });
    if (lines.length > 0) {
      setAnnouncement((current) => ({ key: current.key + 1, text: lines.join(' ') }));
    }
  }

  useEffect(() => {
    const index = focusAt.current;
    if (index === null) return;
    focusAt.current = null;
    const buttons = listRef.current?.querySelectorAll<HTMLElement>('[data-attachment-remove]');
    const target = buttons?.[Math.min(index, buttons.length - 1)];
    if (target !== undefined) target.focus();
    else onEmpty();
  }, [items, onEmpty]);

  function remove(key: number) {
    focusAt.current = items.findIndex((item) => item.key === key);
    attachments.remove(key);
  }

  const live = <LiveRegion key={announcement.key} text={announcement.text} />;
  if (items.length === 0 && refusals.length === 0 && !attachments.bankFile) return live;
  return (
    <Stack sx={{ gap: 1 }} data-attachment-tray="">
      {live}
      {refusals.length > 0 && (
        <Alert severity="warning" data-attachment-refused="" onClose={attachments.dismissRefusals}>
          <Box component="ul" sx={{ m: 0, paddingInlineStart: 2 }}>
            {refusals.map((refusal, index) => (
              <li key={`${refusal.name}-${index}`}>
                {t('assistant.attach.refused', {
                  name: refusal.name,
                  reason: refusalText(t, refusal, locale),
                })}
              </li>
            ))}
          </Box>
        </Alert>
      )}
      {items.length > 0 && (
        <Stack
          ref={listRef}
          direction="row"
          role="list"
          aria-label={t('assistant.attach.listLabel')}
          // One row that scrolls sideways, so the transcript keeps its height.
          sx={{
            gap: 1,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            alignItems: 'flex-start',
            pb: 0.5,
          }}
        >
          {items.map((item) => (
            <Box role="listitem" key={item.key} sx={{ flexShrink: 0 }}>
              <PendingChip
                item={item}
                locked={locked}
                onRemove={() => remove(item.key)}
                onReattach={() => attachments.reattach(item.key)}
              />
            </Box>
          ))}
        </Stack>
      )}
      {items.length > 0 && (
        <Typography sx={{ fontSize: 12, color: tokens.text2 }} data-attach-line="">
          {t('assistant.attach.line')}
        </Typography>
      )}
      {attachments.bankFile && (
        <Typography sx={{ fontSize: 12, color: tokens.text }} data-bank-file="">
          {t('assistant.attach.bankFile')}{' '}
          <Link component={RouterLink} to="/me/accounts">
            {t('assistant.attach.bankFileLink')}
          </Link>
        </Typography>
      )}
    </Stack>
  );
}
